// Vendor imports
const _ = require('lodash')
const { createSelector } = require('reselect')
// Shared imports
const nameUtils = require('shared/name-utils')
// Client V2 common imports
const { FetchStateEnum } = require('client-v2/common/utils/enums')
const collectionModelRecord = require('client-v2/common/models/collection/record')

const containerState = state => state.collectionSelectModal

const isVisible = createSelector(
  containerState,
  s => s ? s.isVisible : false
)

const anchorId = createSelector(
  containerState,
  s => s ? s.anchorId : undefined
)

const article = createSelector(
  containerState,
  s => s ? s.article : undefined
)

const urlId = createSelector(
  article,
  a => a ? a['url_id'] : undefined
)

const isLightweightModalVisible = createSelector(
  [
    isVisible,
    anchorId
  ],
  (isVisible, anchorId) => !!(isVisible && anchorId)
)

const collections = createSelector(
  [
    containerState,
    isLightweightModalVisible
  ],
  (containerState, isLightweightModalVisible) => {
    if (containerState && containerState.collections) {
      const collections = containerState.collections.filter(item => {
        /**
         * Strip out "recommended" collections that don't actually exist.
         * Lightweight engagement does not have designs for "suggested" collections.
         * @see https://app.asana.com/0/1121261655472708/1124199149549158/f
         */
        return !!item.id
      })
      /**
       * Strip out "General" collection until user has at least 1 other collection.
       * Work around for showing empty state and allowing interaction with General via LWE.
       * Should be removed on decommission of General collections.
       */
      const isInACollection = collections.count(item => !!item.isMixed) > 0
      if (isLightweightModalVisible && !isInACollection && collections.size === 1) {
        return collections.filter(item => {
          return item['mix_type'] !== collectionModelRecord.MIX_TYPE_GENERAL
        })
      }
      return collections
    }
  }
)

const url = createSelector(
  [
    containerState,
    article
  ],
  (containerState, article) => {
    if (containerState && containerState.url) {
      return containerState.url
    }
    if (article && article.url) {
      return article.url
    }
  }
)

const isArticleFailed = createSelector(
  containerState,
  s => s ? s.articleState === FetchStateEnum.FAILED : false
)

const isArticleLoaded = createSelector(
  containerState,
  s => s ? s.articleState === FetchStateEnum.LOADED : false
)

const isCollectionsFailed = createSelector(
  containerState,
  s => s ? s.collectionsState === FetchStateEnum.FAILED : false
)

const isCollectionsLoaded = createSelector(
  containerState,
  s => s ? s.collectionsState === FetchStateEnum.LOADED : false
)

const isCommentPending = createSelector(
  containerState,
  s => s ? s.commentState === FetchStateEnum.LOADING : false
)

const isCreatePending = createSelector(
  containerState,
  s => s ? s.createState === FetchStateEnum.LOADING : false
)

const curatedCollectionIds = createSelector(
  collections,
  collections => {
    if (collections) {
      const collectionIds = []
      collections.forEach(item => {
        if (item.isMixed) {
          collectionIds.push(item.id)
        }
      })
      return collectionIds
    }
  }
)

const curatedCollectionCountsByVisibility = createSelector(
  collections,
  collections => {
    if (collections) {
      const counts = { public: 0, private: 0 }
      collections.forEach(item => {
        if (item.isMixed) {
          if (item.visibility === collectionModelRecord.VISIBILITY_PRIVATE) {
            counts.private += 1
          } else {
            counts.public += 1
          }
        }
      })
      return counts
    }
  }
)

const isMixed = createSelector(
  [
    article,
    curatedCollectionCountsByVisibility
  ],
  (article, curatedCollectionCountsByVisibility) => {
    if (article && curatedCollectionCountsByVisibility) {
      let isMixed = article.hasMix
      if (isMixed) {
        if (!curatedCollectionCountsByVisibility.public) {
          isMixed = !curatedCollectionCountsByVisibility.private
        }
      }
      return isMixed
    }
  }
)

const isUnmixPending = createSelector(
  containerState,
  s => s ? s.unmixState === FetchStateEnum.LOADING : false
)

const isUnmixVisible = createSelector(
  [
    isCollectionsLoaded,
    isMixed,
    curatedCollectionCountsByVisibility
  ],
  (isCollectionsLoaded, isMixed, curatedCollectionCountsByVisibility) => (
    isCollectionsLoaded && isMixed && !curatedCollectionCountsByVisibility.public
  )
)

const isUnmixPromptVisible = createSelector(
  containerState,
  s => s ? s.isUnmixPromptVisible : false
)

const commentText = createSelector(
  containerState,
  s => s ? s.commentText : ''
)

const filterText = createSelector(
  containerState,
  s => s ? s.filterText : ''
)

const canCreateFromFilter = createSelector(
  filterText,
  filterText => nameUtils.isValidMixName(filterText)
)

const filterSlug = createSelector(
  filterText,
  filterText => nameUtils.prettyUrl(filterText)
)

const filterSlugMatcher = createSelector(
  filterSlug,
  filterSlug => {
    const startsWith = filterSlug.length < 2 ? '^' : ''
    const escapedStr = _.escapeRegExp(filterSlug)
    const regexp = new RegExp(startsWith + escapedStr, 'i')
    return itemSlug => regexp.test(itemSlug)
  }
)

const filteredCollections = createSelector(
  [
    filterSlugMatcher,
    collections
  ],
  (filterSlugMatcher, collections) => {
    if (collections) {
      return collections.filter(item => filterSlugMatcher(item.slug))
    }
  }
)

const isFilteredCollectionsEmpty = createSelector(
  [
    filteredCollections
  ],
  filteredCollections => {
    if (filteredCollections) {
      return filteredCollections.size === 0
    }
  }
)

const isUnfilteredCollectionsEmpty = createSelector(
  [
    collections
  ],
  collections => {
    if (collections) {
      return collections.size === 0
    }
  }
)

module.exports = {
  anchorId,
  article,
  canCreateFromFilter,
  collections,
  commentText,
  containerState,
  curatedCollectionIds,
  curatedCollectionCountsByVisibility,
  filterSlug,
  filterText,
  filteredCollections,
  isArticleFailed,
  isArticleLoaded,
  isCollectionsFailed,
  isCollectionsLoaded,
  isCommentPending,
  isCreatePending,
  isFilteredCollectionsEmpty,
  isMixed,
  isUnfilteredCollectionsEmpty,
  isUnmixPending,
  isUnmixPromptVisible,
  isUnmixVisible,
  isVisible,
  isLightweightModalVisible,
  url,
  urlId
}
